// Where a chat message stops being text and starts being a link.
//
// A hand-kept port of the app's `src-tauri/src/services/link_detect.rs`, which is
// the canonical implementation. The hosted overlay has no Rust, so this runs the
// same policy in the browser; if you change one, change the other, or a viewer
// sees a link in the desktop app and plain text in OBS. The TLD tables and the
// trailing-punctuation set below are copied verbatim.
//
// Three shapes count as a link:
//
//   1. An explicit scheme: https://example.com/x
//   2. A `www.` host:      www.example.com
//   3. A bare domain:      test.fr, example.co.uk/path
//
// Shape 3 is the reason this file exists (reported: "links like test.fr aren't
// recognized"). Matching it on punctuation alone turns ordinary chat into links,
// because "3.5" and "wait...what" and "home.Then" all have a dot between two runs
// of characters. So the last label is checked against a real TLD table instead.
// That still linkifies a few words that are not URLs (`main.rs` and `that.it` are
// both real TLDs), which is where every other chat client lands too.

import type { MessageSegment } from './overlayMessage';

/** Punctuation that belongs to the sentence, not the link: "see test.fr." */
const TRAILING = new Set(['.', ',', '!', '?', ':', ';', "'", '"', ')', ']', '}', '>', '*', '_']);

/** Every ISO 3166-1 alpha-2 country code. */
const CC_TLDS = [
  'ac','ad','ae','af','ag','ai','al','am','ao','aq','ar','as','at','au','aw','ax',
  'az','ba','bb','bd','be','bf','bg','bh','bi','bj','bm','bn','bo','br','bs','bt',
  'bw','by','bz','ca','cc','cd','cf','cg','ch','ci','ck','cl','cm','cn','co','cr',
  'cu','cv','cw','cx','cy','cz','de','dj','dk','dm','do','dz','ec','ee','eg','er',
  'es','et','eu','fi','fj','fk','fm','fo','fr','ga','gd','ge','gf','gg','gh','gi',
  'gl','gm','gn','gp','gq','gr','gs','gt','gu','gw','gy','hk','hm','hn','hr','ht',
  'hu','id','ie','il','im','in','io','iq','ir','is','it','je','jm','jo','jp','ke',
  'kg','kh','ki','km','kn','kp','kr','kw','ky','kz','la','lb','lc','li','lk','lr',
  'ls','lt','lu','lv','ly','ma','mc','md','me','mg','mh','mk','ml','mm','mn','mo',
  'mp','mq','mr','ms','mt','mu','mv','mw','mx','my','mz','na','nc','ne','nf','ng',
  'ni','nl','no','np','nr','nu','nz','om','pa','pe','pf','pg','ph','pk','pl','pm',
  'pn','pr','ps','pt','pw','py','qa','re','ro','rs','ru','rw','sa','sb','sc','sd',
  'se','sg','sh','si','sk','sl','sm','sn','so','sr','ss','st','su','sv','sx','sy',
  'sz','tc','td','tf','tg','th','tj','tk','tl','tm','tn','to','tr','tt','tv','tw',
  'tz','ua','ug','uk','us','uy','uz','va','vc','ve','vg','vi','vn','vu','wf','ws',
  'ye','yt','za','zm','zw',
];

/** Generic TLDs that actually turn up in chat. Deliberately not exhaustive: an
 *  unknown label renders as plain text, which is a far cheaper mistake than
 *  linkifying the second half of every sentence that runs two words together. */
const G_TLDS = [
  'com','net','org','edu','gov','mil','int','info','biz','name','pro','mobi','asia',
  'jobs','travel','cat','tel','xxx','aero','coop','museum','post','app','dev','page',
  'live','tech','space','world','today','news','media','click','link','fun','art',
  'life','love','moe','gay','lol','wtf','ninja','pizza','cloud','digital','agency',
  'studio','design','games','group','plus','team','tools','video','watch','zone',
  'stream','chat','social','community','network','systems','solutions','services','top',
  'vip','win','one','run','cool','fyi','gift','help','host','icu','ink','online',
  'party','press','pub','red','review','rip','rocks','sale','science','sexy','shop',
  'site','store','study','tokyo','trade','uno','website','wiki','work','xyz','yoga',
  'zip','blog','bar','buzz','date','download','email','fit',
];

const TLDS = new Set([...CC_TLDS, ...G_TLDS]);

const ASCII_ONLY = /^[\x00-\x7F]*$/;
const LABEL_OK = /^[A-Za-z0-9-]+$/;
const ALPHA_ONLY = /^[A-Za-z]+$/;

function hasScheme(s: string): boolean {
  const lower = s.toLowerCase();
  return lower.startsWith('http://') || lower.startsWith('https://');
}

/** Does `host` read as a registrable domain (example.com, a.b.co.uk)? */
function isDomain(host: string): boolean {
  // An `@` means a handle or an email address, neither of which we linkify.
  if (!host || host.includes('@') || !ASCII_ONLY.test(host)) return false;
  const labels = host.split('.');
  if (labels.length < 2) return false;
  for (const label of labels) {
    if (!label || label.startsWith('-') || label.endsWith('-') || !LABEL_OK.test(label)) {
      return false;
    }
  }
  // The TLD carries the decision. Alphabetic only, so "3.5" and "1.2.3" are
  // numbers rather than hosts.
  const tld = labels[labels.length - 1];
  if (!ALPHA_ONLY.test(tld)) return false;
  return TLDS.has(tld.toLowerCase());
}

/** Trim sentence punctuation off the end of a word. */
function trimTrailing(word: string): string {
  let end = word.length;
  while (end > 0 && TRAILING.has(word[end - 1])) end--;
  return word.slice(0, end);
}

/** Split one whitespace-delimited word into its link and the punctuation
 *  trailing it. `null` when the word is not a link. */
export function splitLink(word: string): { link: string; trailing: string } | null {
  if (!word) return null;
  const candidate = trimTrailing(word);
  if (!candidate) return null;
  const trailing = word.slice(candidate.length);

  let looksLikeLink: boolean;
  if (hasScheme(candidate)) {
    looksLikeLink = true;
  } else if (candidate.toLowerCase().startsWith('www.')) {
    looksLikeLink = true;
  } else {
    // Bare domain: everything before the first path/query/fragment marker has
    // to read as a host on its own.
    const cut = candidate.search(/[/?#]/);
    looksLikeLink = isDomain(cut === -1 ? candidate : candidate.slice(0, cut));
  }

  return looksLikeLink ? { link: candidate, trailing } : null;
}

/** The href for a link's visible text. A schemeless link is assumed https,
 *  which is what every browser address bar does now. */
export function linkUrl(link: string): string {
  return hasScheme(link) ? link : `https://${link}`;
}

/**
 * Link segments in place of the links inside text segments. The app cuts them
 * in Rust (irc_service.rs for Twitch, text_spans.rs for Kick, YouTube and
 * TikTok) and the renderer colours them with the Links style controls, so the
 * overlay cuts every platform's rows the same way.
 */
export function applyLinks(segments: MessageSegment[]): MessageSegment[] {
  const out: MessageSegment[] = [];
  for (const seg of segments) {
    if (seg.type !== 'text') {
      out.push(seg);
      continue;
    }
    // Keep the whitespace runs (capture group) so spacing survives the split.
    const parts = seg.content.split(/(\s+)/);
    let buffer = '';
    for (const part of parts) {
      const hit = part && !/^\s/.test(part) ? splitLink(part) : null;
      if (hit) {
        if (buffer) {
          out.push({ type: 'text', content: buffer });
          buffer = '';
        }
        out.push({ type: 'link', content: hit.link, url: linkUrl(hit.link) });
        // Sentence punctuation carries on into the text run, so "see test.fr."
        // links the domain and leaves the full stop as text.
        buffer += hit.trailing;
      } else {
        buffer += part;
      }
    }
    if (buffer) out.push({ type: 'text', content: buffer });
  }
  return out;
}
